import type { InfiniteData, QueryClient, QueryClientConfig } from '@tanstack/react-query'

import { resetBadgeEntityProgress } from '@src/mappers/collection/badge-entity.mapper'
import { getTrackDayIds, getTracks } from '@src/mappers/collection/track.mapper'
import {
  buildCompletedEntityVideoHistory,
  clearEntityVideoHistory,
} from '@src/mappers/media/video-history.helpers'
import { ONE_MINUTE } from '@src/utils/constants.util'

/**
 * Prefix for React Query keys backing Cocoa collection fetches (custom + standard slugs).
 * Favorite mutations patch every query whose key starts with this (see FavoriteButton).
 */
export const COCOA_COLLECTION_QUERY_KEY_PREFIX = 'cocoa-collection' as const

export function getCocoaCollectionQueryKey(slug: string, locale: string) {
  return [COCOA_COLLECTION_QUERY_KEY_PREFIX, slug, locale] as const
}

export function getRecentlyWatchedQueryKey(locale: string) {
  return ['recently-watched', locale] as const
}

// Own prefix: this caches a mapped view model, and FavoriteButton patches assume the raw shape.
export function getQuizResultsQueryKey(locale: string) {
  return ['quiz-results', locale] as const
}

export function getQuizFeaturedVideosQueryKey(slug: string, locale: string) {
  return ['quiz-featured-videos', slug, locale] as const
}

export const ENTITY_QUERY_KEY_PREFIX = 'entity' as const

// A track entity's videoHistory is scoped to the requested day, so dayId is part of the key.
export function getEntityQueryKey(entityId: string, locale: string, dayId?: string) {
  return [ENTITY_QUERY_KEY_PREFIX, entityId, locale, dayId ?? null] as const
}

// Only seeds never-fetched queries so an invalidated entry isn't revived as fresh data.
export function seedEntityQueryData(
  queryClient: QueryClient,
  key: ReturnType<typeof getEntityQueryKey>,
  item: CocoApi.PlayableEntity | CocoApi.CustomCollectionEntity
) {
  if (!queryClient.getQueryState(key)) {
    queryClient.setQueryData(key, item)
  }
}

/* Create a react-query key for parent content document */
export function getContentDetailsQueryKey(
  contentType: ParentContentTypes,
  slug: string | undefined,
  locale: string
) {
  if (!slug) return []
  return [contentType, slug, locale] as const
}

export function invalidateContentDetails(
  queryClient: QueryClient,
  parentContent: ParentContent,
  locale: string
) {
  const queryKey = getContentDetailsQueryKey(
    parentContent._type as ParentContentTypes,
    parentContent.slug,
    locale
  )
  if (!queryKey.length) return Promise.resolve()
  return queryClient.invalidateQueries({ queryKey })
}

export function invalidateRecentlyWatched(queryClient: QueryClient, locale: string) {
  return queryClient.invalidateQueries({ queryKey: getRecentlyWatchedQueryKey(locale) })
}

/** Optimistic, so track checkmarks update before COCOA reflects the workout. */
export function patchVideoHistoryInContentQueryState(
  prev: ParentContent | null | undefined,
  entityId: string,
  dayId?: string
): ParentContent | null | undefined {
  if (!prev) return prev

  const nested = prev.entities?.[entityId] as CocoApi.PlayableEntity | undefined
  if (!nested) return prev

  return {
    ...prev,
    entities: {
      ...prev.entities,
      [entityId]: {
        ...nested,
        userData: {
          ...nested.userData,
          videoHistory: buildCompletedEntityVideoHistory(nested.userData?.videoHistory, dayId),
        },
      } as CocoApi.Entity,
    },
  }
}

const clearEntityHistory = (
  entity: CocoApi.Entity | undefined,
  dayIds?: string[]
): CocoApi.Entity | undefined => {
  const playable = entity as CocoApi.PlayableEntity | undefined
  if (!playable?.userData?.videoHistory) return entity

  return {
    ...playable,
    userData: {
      ...playable.userData,
      videoHistory: clearEntityVideoHistory(playable.userData.videoHistory, dayIds),
    },
  } as CocoApi.Entity
}

// With a trackId only that track's days and badge are cleared, so sibling tracks keep progress.
// Without one, all playable history and every active badge's progress are cleared.
export function resetVideoHistoryInContentQueryState(
  prev: ParentContent | null | undefined,
  trackId?: string
): ParentContent | null | undefined {
  if (!prev?.entities) return prev

  const track = trackId ? getTracks(prev).find((t) => t._id === trackId) : undefined
  const entities: CocoApi.Entities = { ...prev.entities }

  if (track) {
    const dayIds = getTrackDayIds(track)
    const itemIds = new Set(
      (track.segments ?? []).flatMap((segment) =>
        (segment.days ?? []).flatMap((day) => (day.items ?? []).map((item) => item._id))
      )
    )

    for (const id of itemIds) {
      const cleared = clearEntityHistory(entities[id], dayIds)
      if (cleared) entities[id] = cleared
    }

    if (track.badgeId) {
      const badge = entities[track.badgeId] as CocoApi.BadgeEntity | undefined
      if (badge?._type === 'badge') {
        entities[track.badgeId] = resetBadgeEntityProgress(badge) as CocoApi.Entity
      }
    }

    return { ...prev, entities }
  }

  for (const [key, entity] of Object.entries(prev.entities)) {
    const badge = entity as CocoApi.BadgeEntity | undefined

    if (badge?._type !== 'badge') {
      const cleared = clearEntityHistory(entity)
      if (cleared) entities[key] = cleared
      continue
    }

    if (badge.status === 'active') entities[key] = resetBadgeEntityProgress(badge) as CocoApi.Entity
  }

  return { ...prev, entities }
}

/**
 * Updates `userData.isFavorited` for an entity inside a collection module's `entities` map.
 */
export function patchFavoriteInCollectionQueryState(
  prev: CocoApi.CollectionModule | null | undefined,
  entityId: string,
  nextIsFavorited: boolean
): CocoApi.CollectionModule | null | undefined {
  if (!prev) return prev

  const nested = prev.entities?.[entityId]
  if (!nested) return prev

  return {
    ...prev,
    entities: {
      ...prev.entities,
      [entityId]: {
        ...nested,
        userData: { ...nested.userData, isFavorited: nextIsFavorited },
      } as CocoApi.Entity,
    },
  }
}

/**
 * Updates `userData.isFavorited` for an entity across all pages in an infinite collection query.
 */
export function patchFavoriteInInfiniteCollectionQueryState(
  prev: InfiniteData<CocoApi.CollectionModule> | null | undefined,
  entityId: string,
  nextIsFavorited: boolean
): InfiniteData<CocoApi.CollectionModule> | null | undefined {
  if (!prev) return prev

  return {
    ...prev,
    pages: prev.pages.map(
      (page) => patchFavoriteInCollectionQueryState(page, entityId, nextIsFavorited) ?? page
    ),
  }
}

export type ParentContentTypes = Exclude<CocoApi.ContentType, CocoApi.ContentType.customCollection>
export type ParentContent = CocoApi.Program | CocoApi.Compilation | CocoApi.BODiBlock

/**
 * Updates `userData.isFavorited` on the parent entity (`_id`) or on a nested `entities[id]`
 * entry. Returns `prev` unchanged if `entityId` does not match either.
 */
export function patchFavoriteInContentQueryState(
  prev: ParentContent | null | undefined,
  entityId: string,
  nextIsFavorited: boolean
): ParentContent | null | undefined {
  if (!prev) return prev

  if (prev._id === entityId) {
    return {
      ...prev,
      userData: { ...prev.userData, isFavorited: nextIsFavorited },
    }
  }

  const nested = prev.entities?.[entityId]
  if (!nested) return prev

  return {
    ...prev,
    entities: {
      ...prev.entities,
      [entityId]: {
        ...nested,
        userData: { ...nested.userData, isFavorited: nextIsFavorited },
      } as CocoApi.Entity,
    },
  }
}

export function patchFavoriteInEntityQueryState(
  prev: CocoApi.Entity | null | undefined,
  nextIsFavorited: boolean
): CocoApi.Entity | null | undefined {
  if (!prev) return prev
  return { ...prev, userData: { ...prev.userData, isFavorited: nextIsFavorited } } as CocoApi.Entity
}

export const defaultStaleTime = ONE_MINUTE * 10

export const defaultQueryOptions = {
  staleTime: defaultStaleTime,
  retry: false,
  refetchOnReconnect: false,
  refetchOnWindowFocus: false,
}

export const defaultQueryClientConfigs: QueryClientConfig = {
  defaultOptions: {
    queries: {
      staleTime: defaultStaleTime,
    },
  },
}
